/**
 * AdditionalCharges Component
 *
 * This component allows users to review and add additional charges (e.g., damage fees)
 * to a customer's chargebee subscription before proceeding to the next step.
 *
 * It fetches available addon charge options from Chargebee based on the customer's country
 * and vehicle group ancestry. These addons are then displayed in a modal for the user to select from.
 *
 * The component displays:
 * - A loading spinner while addon data is being fetched.
 * - A table showing currently selected additional charges.
 * - A button to open a modal for adding new charges.
 * - A submit button to proceed to the next screen.
 *
 * Props:
 * - navigateTarget: the route to navigate to after submission
 * - currentVehicleState: contains current vehicle-related context (e.g., group ancestry)
 * - additionalChargesState: the state object managing the list of additional charges
 * - additionalChargesDispatch: a dispatch function to update additionalChargesState in the parent
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Label from "@components/inputs/text/Label";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { NavBar } from "@components/navigation/NavBar/NavBar";

import SubscriptionService from "@services/subscription/SubscriptionService";
import { AddonDetails } from "@services/subscription/SubscriptionService";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Country } from "@utils/constants/localisation";
import { CurrentVehicleState } from "./currentVehicleDetailsReducer";
import { AdditionalChargesAction, AdditionalChargesState } from "./additionalChargesReducer";
import AddChargeModal from "./AdditionalChargesModal";
import Button, { ButtonStyle } from "@components/buttons/Button/Button";
import ChargesTable from "./ChargesTable";
import LoadingSpinner from "@utils/loadingSpinner";
import Warning from "@components/modal/info/Warning";
import { getRootGroupFromAncestry } from "@utils/misc/functions";

type AdditionalChargesProps = {
    navigateTarget: string;
    currentVehicleState: CurrentVehicleState;
    additionalChargesState: AdditionalChargesState
    additionalChargesDispatch: React.Dispatch<AdditionalChargesAction>
};

export default function AdditionalCharges({
    navigateTarget,
    currentVehicleState,
    additionalChargesState,
    additionalChargesDispatch
}: AdditionalChargesProps) {

    const { custId, country } = useParams() as { custId: string; country: Country };
    const navigate = useNavigate();
    const msalContext = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [availableAddons, setAvailableAddons] = useState<AddonDetails[]>([]);
    const [addonsLoading, setAddonsLoading] = useState<boolean>(true);
    const [addonsError, setAddonsError] = useState<boolean>(false);
    const [addonsErrMsg, setAddonsErrMsg] = useState("");

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);


    // Fetch available addons on component mount
    useEffect(() => {
        const fetchAddons = async () => {
            try {
                const requestPayload: any = {
                    country: country,
                    accounting_prefix: ["405", "406", "2307"].join(',')
                };
                if (country === Country.AU) {
                    // Group ancestry is a string like `NSW / Sydney`. - Including group root and subgroups.
                    // The group root is the part we need for the state in the addons payload
                    requestPayload.state = getRootGroupFromAncestry(currentVehicleState.vehicle.group_ancestry);
                }
                const addonsData = await SubscriptionService.getAddons(
                    requestPayload,
                    {
                        msalContext: msalContext,
                        isAuthenticated: isAuthenticated
                    }
                );

                setAvailableAddons(addonsData);
            } 
            catch (error) {
                setAddonsError(true);
                setAddonsErrMsg("Something went wrong");
            }
            finally {
                setAddonsLoading(false);
            }
        };
        fetchAddons();
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        navigate(navigateTarget);
    };

    return (
        <>
            <NavBar pageTitle="Charges" />

            {addonsLoading ? (
                <div style={{ margin: "2.5vh 2.5vw", textAlign: "center" }}>
                    <LoadingSpinner testId="loading-spinner" />
                </div>
            ) : addonsError ? (
                <Warning message='Something went wrong' additionalInfo={addonsErrMsg} onClickClose={() => navigate('/')} />
            ) : availableAddons && (
                <>
                    <form style={{ margin: "2.5vh 2.5vw" }} onSubmit={handleSubmit}>
                        <div style={{ display: "flex", flexDirection: "column", marginBottom: "2.5vh" }}>
                            <Label text={
                                <>
                                    Please add any <strong>additional charges (e.g. damage):</strong>
                                </>
                            } />
                        </div>

                        <div style={{ margin: '2.5vh 0vw' }}>
                            <ChargesTable
                                chargesList={additionalChargesState.chargesList}
                                onRemove={(index) =>
                                    additionalChargesDispatch({ type: "REMOVE_SELECTED_ADDON_FROM_CHARGES", payload: index })
                                }
                                country={country}
                            />
                        </div>

                        <div style={{ margin: '2.5vh 0vw' }}>
                            <Button
                                label="Add charge"
                                btnType={ButtonStyle.secondary}
                                onClickFunc={() => setIsModalOpen(true)}
                                testId="add-charge-button"
                            />
                        </div>

                        <div style={{ margin: '2.5vh 0vw' }}>
                            <SubmitBtn text="Next" />
                        </div>
                    </form>

                    <AddChargeModal
                        country={country}
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        addonOptions={availableAddons}
                        dispatch={additionalChargesDispatch}
                    />
                </>
            )}
        </>
    );
}